/*
 *
 *  Tooltip replacement for PrimeVue tooltip that doesn't work with our editor iframe.
 *
 */
import { uuidv4 } from "../utils/string";
import { fadeIn } from "../utils/dom";

const tooltipRegistry = new Set(); // ✅ Global registry

function onMouseEnter(e) {
    show(e.currentTarget);
}

function onMouseLeave(e) {
    hide(e.currentTarget);
}

function onClick(e) {
    hide(e.currentTarget);
}

function bindEvents(el) {
    el.addEventListener("mouseenter", onMouseEnter);
    el.addEventListener("mouseleave", onMouseLeave);
    el.addEventListener("click", onClick);
}

function unbindEvents(el) {
    el.removeEventListener("mouseenter", onMouseEnter);
    el.removeEventListener("mouseleave", onMouseLeave);
    el.removeEventListener("click", onClick);
}

function show(el) {
    let node = create(el);
    if (!node) return;

    setTimeout(() => {
        node.style.display = "inline-block";
        fadeIn(node, 250);
    }, node.delay);
}

function hide(el) {
    const node = document.getElementById(el._tooltipId);
    if (node) {
        tooltipRegistry.delete(node); // ✅ Untrack
        if (node.parentNode) {
            node.parentNode.removeChild(node);
        }
    }
}

function updateLabel(node, label) {
    if (node?.lastChild) {
        node.lastChild.innerHTML = label;
    }
}

function align(el, tooltipElement, pos) {
    var rect = el.getBoundingClientRect();

    const space = 5;
    const styles = tooltipElement.style;
    const tooltipWidth = tooltipElement.offsetWidth;
    const tooltipHeight = tooltipElement.offsetHeight;

    const top = rect.top + window.scrollY;
    const left = rect.left + window.scrollX;

    const { width: elementWidth, height: elementHeight } = el.getBoundingClientRect();

    styles.position = "absolute";
    styles.zIndex = "1000000";

    if (pos === "left" || pos === "right") {
        styles.top = top + parseInt((elementHeight - tooltipHeight) / 2) + "px";

        if (pos === "left") {
            styles.left = left - tooltipWidth - space + "px";
        } else {
            styles.left = left + elementWidth + space + "px";
        }
    } else if (pos === "top" || pos === "bottom") {
        styles.left = left + parseInt((elementWidth - tooltipWidth) / 2) + "px";

        if (pos === "top") {
            styles.top = top - tooltipHeight - space + "px";
        } else {
            styles.top = top + elementHeight + space + "px";
        }
    }
}

function create(el) {
    const label = el.dataset.tooltipContent
        ? document.querySelector(el.dataset.tooltipContent)?.innerHTML
        : el.dataset.tooltipLabel;

    if (!label) {
        return;
    }

    const id = uuidv4() + "_tooltip";

    el._tooltipId = id;
    const position = el.dataset.tooltipPosition ?? "top";
    const disableArrow = el.dataset.tooltipArrow === "false";
    const delay = el.dataset.tooltipDelay ? parseInt(el.dataset.tooltipDelay) : 500;

    let node = document.createElement("div");
    node.className = `a-tooltip a-component a-tooltip-${position} ${el.dataset.tooltipTheme}${disableArrow ? " a-tooltip-no-arrow" : ""}`;
    node.id = id;
    node.innerHTML = "<div class='a-tooltip-arrow'></div><div class='a-tooltip-text'></div>";
    node.delay = delay;

    updateLabel(node, label);

    document.body.appendChild(node);
    tooltipRegistry.add(node); // ✅ Track

    node.style.display = "inline-block";
    align(el, node, position);
    node.style.display = "none";

    return node;
}

export function clearAllTooltips() {
    tooltipRegistry.forEach((node) => {
        if (node?.parentNode) {
            node.parentNode.removeChild(node);
        }
    });
    tooltipRegistry.clear();
}

export function initTooltip(el, binding) {
    let label = undefined;
    if (!el.dataset.tooltipContent) {
        if (binding.value === undefined) {
            console.warn("v-tooltip needs to be passed as String. Example: v-tooltip=\"'Value'\"");
        }
        label = binding.value;
    }

    if ((label + "").length === 0) {
        return;
    }

    el.setAttribute("data-tooltip-label", label);
    bindEvents(el);
}

const Tooltip = {
    beforeMount: initTooltip,
    unmounted(el) {
        unbindEvents(el);
        hide(el); // ✅ Clean up tooltip if still visible
    },
    updated(el, options) {
        // Tooltip updates can be handled here if dynamic label is supported
        // const node = document.getElementById(el._tooltipId);
        // node && updateLabel(node, options.value);
    },
};

export default Tooltip;
